import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import EvidenceCard from "../components/EvidenceCard";
import Marquee from "../components/Marquee";

const ACCENT = "#FFCC00";

const OSHA_IMG =
  "https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const WAGE_IMG =
  "https://images.unsplash.com/photo-1766218331975-ecc29597ce9f?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const WORK_IMG =
  "https://images.unsplash.com/photo-1521791055366-0d553872952f?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

export default function ImpactWorkers() {
  return (
    <div style={{ backgroundColor: "#050505", paddingTop: "64px" }}>
      {/* Header */}
      <div
        data-testid="workers-header"
        style={{
          padding: "64px 24px 56px",
          borderBottom: "1px solid #1A1A1A",
          backgroundColor: "#0A0A0A",
        }}
      >
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <p
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: "11px",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: ACCENT,
              marginBottom: "16px",
            }}
          >
            Page 04 — Evidence
          </p>
          <h1
            style={{
              fontFamily: "'Cabinet Grotesk', sans-serif",
              fontSize: "clamp(2rem, 5vw, 3.8rem)",
              fontWeight: 900,
              letterSpacing: "-0.03em",
              color: "#FFFFFF",
              marginBottom: "16px",
              lineHeight: 1.05,
              borderLeft: `4px solid ${ACCENT}`,
              paddingLeft: "24px",
            }}
          >
            Impact on Workers
          </h1>
          <p
            style={{
              fontSize: "16px",
              color: "#71717A",
              maxWidth: "680px",
              lineHeight: 1.65,
              paddingLeft: "28px",
            }}
          >
            Labor regulations are some of the most powerful and contested tools
            in the government's economic arsenal — directly shaping how much
            workers earn, how safe their workplaces are, and what protections
            they can count on.
          </p>
        </div>
      </div>

      <Marquee />

      <div
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          padding: "64px 24px",
          display: "grid",
          gridTemplateColumns: "280px 1fr",
          gap: "64px",
          alignItems: "start",
        }}
        className="impact-layout"
      >
        {/* Sticky Commentary */}
        <aside
          data-testid="workers-commentary"
          style={{ position: "sticky", top: "88px" }}
        >
          <p
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: "10px",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: "#52525B",
              marginBottom: "16px",
            }}
          >
            Student Commentary
          </p>
          <p
            style={{
              color: "#D1D1D6",
              fontSize: "14px",
              lineHeight: 1.8,
              marginBottom: "20px",
            }}
          >
            Of the three groups I studied, workers might be the ones most
            directly affected by regulation in their daily lives — yet many
            people don't realize just how much labor law shapes their
            experience at work.
          </p>
          <p
            style={{
              color: "#D1D1D6",
              fontSize: "14px",
              lineHeight: 1.8,
              marginBottom: "20px",
            }}
          >
            The OSHA statistics were genuinely surprising to me. A 60% drop in
            workplace fatalities since 1970 is an enormous achievement, and it
            happened because of a specific government regulation — not market
            forces. Companies did not voluntarily make their workplaces safer.
          </p>
          <p
            style={{
              color: "#D1D1D6",
              fontSize: "14px",
              lineHeight: 1.8,
            }}
          >
            The minimum wage debate is much more complex. The CBO data showing
            that a $15 minimum wage could both lift people out of poverty
            and eliminate jobs at the same time really challenged my thinking
            about simple solutions to complex problems.
          </p>

          <div style={{ marginTop: "36px", padding: "20px", backgroundColor: "#0D0D0D", border: "1px solid #1A1A1A" }}>
            <p
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: "10px",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "#52525B",
                marginBottom: "12px",
              }}
            >
              Research Question
            </p>
            <p style={{ color: "#A1A1AA", fontSize: "13px", lineHeight: 1.7, fontStyle: "italic" }}>
              "How does government regulation affect businesses, workers, and
              consumers in the U.S. economy?"
            </p>
          </div>
        </aside>

        {/* Evidence Cards */}
        <div data-testid="workers-evidence">
          <EvidenceCard
            artifactNum={1}
            title="OSHA and the Dramatic Decline in Workplace Fatalities"
            imageUrl={OSHA_IMG}
            imageAlt="Construction workers at a worksite, representing OSHA safety regulations"
            accentColor={ACCENT}
            caption="Workplace fatality data from the Bureau of Labor Statistics shows a dramatic decline since OSHA's creation in 1970 — one of the clearest examples of a government regulation saving lives."
            citation="[4]"
          >
            <p style={{ marginBottom: "14px" }}>
              The Occupational Safety and Health Administration (OSHA) was
              established in 1970 after a wave of industrial accidents and
              growing worker advocacy. Before OSHA, workplace fatalities in the
              United States exceeded{" "}
              <strong style={{ color: "#FFFFFF" }}>14,000 per year</strong>. By
              2022, that number had fallen to approximately{" "}
              <strong style={{ color: "#FFFFFF" }}>5,486 deaths</strong> — a
              reduction of over 60%, even as the U.S. workforce more than
              doubled in size.
              <sup style={{ color: ACCENT }}>[4]</sup>
            </p>
            <p style={{ marginBottom: "14px" }}>
              OSHA achieves this through mandatory safety standards (hard hats,
              guardrails, equipment training), regular workplace inspections,
              and financial penalties for violations. Employers know that
              failing to follow safety rules can result in fines or forced
              shutdowns — which creates a powerful incentive to comply.
            </p>
            <p>
              Critics of OSHA sometimes argue that the market would naturally
              produce safe workplaces — workers would leave dangerous employers
              and demand higher wages to compensate for risk. But the pre-1970
              data tells a different story: without regulation, many employers
              cut corners on safety because workers in low-income or rural areas
              often had no alternative employment options.
            </p>
          </EvidenceCard>

          <EvidenceCard
            artifactNum={2}
            title="The Minimum Wage Debate: Poverty Relief vs. Job Loss"
            imageUrl={WAGE_IMG}
            imageAlt="Workers and economic data representing the minimum wage debate"
            accentColor={ACCENT}
            caption="The Congressional Budget Office's 2019 analysis of a $15 federal minimum wage found both significant benefits and significant costs — a perfect example of the trade-offs inherent in labor regulation."
            citation="[5]"
          >
            <p style={{ marginBottom: "14px" }}>
              The federal minimum wage, currently set at{" "}
              <strong style={{ color: "#FFFFFF" }}>$7.25 per hour</strong> (and
              unchanged since 2009), is one of the most debated forms of labor
              regulation. Many states and cities have set their own higher
              minimums — California's is $16/hour, and Washington, D.C.'s is
              $17/hour — creating a patchwork of regulations across the
              country.
              <sup style={{ color: ACCENT }}>[5]</sup>
            </p>
            <p style={{ marginBottom: "14px" }}>
              A 2019 Congressional Budget Office (CBO) analysis of a proposed
              $15 federal minimum wage found that it would lift approximately{" "}
              <strong style={{ color: "#FFFFFF" }}>1.3 million people</strong>{" "}
              out of poverty — but could also result in the elimination of
              approximately{" "}
              <strong style={{ color: "#FFFFFF" }}>1.3 million jobs</strong> as
              businesses reduce hiring or automate tasks.
            </p>
            <p>
              This trade-off is a classic example of why economists disagree so
              sharply about minimum wage laws. Some workers would earn more;
              others might lose their jobs entirely. The outcome depends heavily
              on local economic conditions, which is part of why many economists
              favor allowing states and cities to set their own minimums rather
              than imposing a single national rate.
            </p>
          </EvidenceCard>

          <EvidenceCard
            artifactNum={3}
            title="The Family and Medical Leave Act: Who Really Benefits?"
            imageUrl={WORK_IMG}
            imageAlt="Workers in a modern office setting"
            accentColor={ACCENT}
            caption="The FMLA (1993) guarantees up to 12 weeks of job-protected leave for family and medical reasons — but its unpaid structure limits its usefulness for lower-income workers who can't afford to go without a paycheck."
            citation="[6]"
          >
            <p style={{ marginBottom: "14px" }}>
              Signed by President Clinton in 1993, the Family and Medical Leave
              Act (FMLA) requires employers with 50 or more employees to provide
              up to{" "}
              <strong style={{ color: "#FFFFFF" }}>12 weeks of unpaid,
              job-protected leave</strong> for childbirth, adoption, serious
              illness, or care of a family member.
              <sup style={{ color: ACCENT }}>[6]</sup>
            </p>
            <p style={{ marginBottom: "14px" }}>
              Research shows that the FMLA has been particularly beneficial for
              working mothers, helping reduce the "maternal penalty" — the wage
              and career gap women face after having children. By guaranteeing
              they can return to the same job after leave, the law reduces the
              risk of losing income and career progress due to family
              responsibilities.
            </p>
            <p>
              However, a significant limitation is that the leave is
              <strong style={{ color: "#FFFFFF" }}> unpaid</strong>. Workers who
              cannot afford to go without income for three months — often those
              in lower-wage jobs — frequently cannot take advantage of the law.
              This means the FMLA effectively benefits middle- and upper-class
              workers far more than those who need support the most. It's an
              example of how a well-intentioned regulation can have unequal
              real-world effects.
            </p>
          </EvidenceCard>

          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginTop: "16px" }}>
            <Link
              to="/impact-consumers"
              data-testid="next-page-consumers"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: "#007AFF",
                color: "#FFFFFF",
                padding: "12px 24px",
                fontSize: "13px",
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              Next: Impact on Consumers <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .impact-layout {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
